import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Wallet, Plus, Download, Loader2, Calendar, Search, Trash2, Pencil,
  CheckCircle2, X, Users, CalendarClock, TrendingUp, FileText
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { SignatureField } from '../components/SignatureField';

function formatFCFA(amount) {
  const v = Number(amount || 0);
  return v.toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';
}

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
}

const DEFAULT_SOCIAL = [
  { label: 'INPS part salariale (3.60%)', base: null, rate: 3.6, amount: 0 },
  { label: 'AMO part salariale (3.06%)', base: null, rate: 3.06, amount: 0 },
];

const DEFAULT_ALLOWANCES = [
  { label: 'Prime de transport', amount: 0 },
];

const Payslips = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin === true;

  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editingPayslip, setEditingPayslip] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);

  // ==== Accès sécurisé au module (PIN) ====
  const [accessUnlocked, setAccessUnlocked] = useState(false);
  const [accessPin, setAccessPin] = useState('');
  const [pinConfigured, setPinConfigured] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/hr/payslips-access/status');
        if (r.data.bypass) {
          setAccessUnlocked(true);
        }
        setPinConfigured(r.data.pin_configured);
      } catch {
        // continuer en verrouillé
      } finally {
        setCheckingAccess(false);
      }
    })();
  }, []);

  const unlockAccess = async () => {
    if (!/^\d{4}$/.test(accessPin)) return toast.error('PIN 4 chiffres attendu');
    try {
      await api.post('/hr/payslips-access/verify-pin', { pin: accessPin });
      setAccessUnlocked(true);
      toast.success('Accès autorisé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PIN incorrect');
    }
  };

  // ==== Dialogue Attestation Employeur ====
  const [attestOpen, setAttestOpen] = useState(false);
  const [attestAgent, setAttestAgent] = useState(null);
  const [attestForm, setAttestForm] = useState({ signatory_name: '', signatory_title: '', employee_id: '', reason: '', signature_base64: '' });
  const [attestLoading, setAttestLoading] = useState(false);

  const openAttestationDialog = async (agent) => {
    setAttestAgent(agent);
    let defaultSig = '';
    const defaultTitle = 'Directeur Général';
    try {
      const s = await api.get('/agency-settings');
      defaultSig = s.data?.legal_representative || s.data?.name || '';
    } catch { /* ignore */ }
    // Récupère la dernière signature manuscrite mémorisée
    const savedSignature = localStorage.getItem('gab_last_signature_image') || '';
    setAttestForm({
      signatory_name: defaultSig,
      signatory_title: defaultTitle,
      employee_id: agent.employee_id || '',
      reason: '',
      signature_base64: savedSignature,
    });
    setAttestOpen(true);
  };

  const submitAttestation = async () => {
    if (!attestAgent) return;
    if (!attestForm.signatory_name.trim()) {
      toast.error('Nom du signataire requis');
      return;
    }
    setAttestLoading(true);
    try {
      const body = {
        signatory_name: attestForm.signatory_name.trim(),
        signatory_title: attestForm.signatory_title.trim(),
        employee_id: attestForm.employee_id.trim(),
        reason: attestForm.reason.trim(),
        signature_base64: attestForm.signature_base64 || null,
      };
      const r = await api.post(`/hr/employer-attestation/${attestAgent.id}`, body, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `attestation_employeur_${(attestAgent.name || 'salarie').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Attestation téléchargée');
      setAttestOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setAttestLoading(false);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [agentsRes, payslipsRes] = await Promise.all([
        api.get('/users'),
        api.get('/payslips', { params: { period: selectedMonth } }),
      ]);
      setAgents(agentsRes.data.filter(u => ['agent', 'admin', 'super_admin'].includes(u.role)));
      setPayslips(payslipsRes.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedMonth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const payslipByUserId = useMemo(() => {
    const m = {};
    for (const p of payslips) m[p.user_id] = p;
    return m;
  }, [payslips]);

  const filteredAgents = useMemo(() => {
    return agents.filter(a => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return a.name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s);
    });
  }, [agents, searchTerm]);

  const totals = useMemo(() => {
    const totalNet = payslips.reduce((s, p) => s + (p.net_salary || 0), 0);
    const totalGross = payslips.reduce((s, p) => s + (p.gross_salary || 0), 0);
    const paidCount = payslips.filter(p => p.paid).length;
    return { totalNet, totalGross, paidCount, total: payslips.length };
  }, [payslips]);

  const handleOpenEdit = (agent, existingPayslip) => {
    setEditingAgent(agent);
    setEditingPayslip(existingPayslip || {
      user_id: agent.id,
      period: selectedMonth,
      base_salary: 0,
      allowances: DEFAULT_ALLOWANCES.map(a => ({ ...a })),
      deductions: [],
      social_contributions: DEFAULT_SOCIAL.map(a => ({ ...a })),
      paid_leave_days: 0,
      paid_leave_balance: null,
      paid_leave_amount: 0,
      notes: '',
      paid: false,
      payment_method: 'virement',
    });
    setEditOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le bulletin de ${name} ?`)) return;
    try {
      await api.delete(`/payslips/${id}`);
      toast.success('Supprime');
      fetchAll();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDownloadPDF = async (id, name) => {
    try {
      const res = await api.get(`/payslips/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin_${(name || '').replace(/\s/g, '_')}_${selectedMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur');
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-20" data-testid="payslips-forbidden">
        <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Reserve aux administrateurs</p>
      </div>
    );
  }

  // Ecran de verrouillage PIN (non-super-admin)
  if (!checkingAccess && !accessUnlocked) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="payslips-locked">
        <div className="max-w-md w-full bg-white rounded-2xl border-2 border-[#B85C38]/30 shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#B85C38]/10 flex items-center justify-center mb-4">
            <Wallet className="w-8 h-8 text-[#B85C38]" />
          </div>
          <h2 className="text-xl font-bold text-[#2B3A2C] mb-2">Module Bulletins de Paie</h2>
          <p className="text-sm text-gray-600 mb-5">
            Ce module contient des informations confidentielles. Saisissez le code d&apos;accès (4 chiffres).
          </p>
          {!pinConfigured && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs text-amber-800">
              Aucun PIN n&apos;a encore été configuré. Le Super Administrateur peut en définir un depuis les paramètres.
            </div>
          )}
          <input
            type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
            value={accessPin}
            onChange={(e) => setAccessPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => e.key === 'Enter' && unlockAccess()}
            className="text-center text-2xl font-mono tracking-[0.6em] w-40 mx-auto border-2 border-[#B85C38]/40 rounded-lg py-3 focus:outline-none focus:border-[#B85C38]"
            placeholder="••••"
            data-testid="payslips-pin-input"
          />
          <div className="mt-4">
            <button
              onClick={unlockAccess}
              disabled={accessPin.length !== 4}
              className="bg-[#B85C38] hover:bg-[#8F4426] text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="payslips-unlock-btn"
            >
              Déverrouiller
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>;
  }

  const monthsOpts = monthOptions();

  return (
    <div className="space-y-6" data-testid="payslips-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <Wallet className="w-7 h-7 text-[#1B3A4B]" />
            Bulletins de Paie
          </h1>
          <p className="text-gray-500 mt-1">Gerez les salaires mensuels de vos collaborateurs</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[220px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthsOpts.map(o => <SelectItem key={o.value} value={o.value} className="capitalize">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Bulletins generes', value: `${totals.total}/${agents.length}`, icon: Users, color: 'text-[#1B3A4B]' },
          { label: 'Bulletins payes', value: `${totals.paidCount}/${totals.total}`, icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Masse salariale brute', value: formatFCFA(totals.totalGross), icon: Wallet, color: 'text-amber-600' },
          { label: 'Masse salariale nette', value: formatFCFA(totals.totalNet), icon: Wallet, color: 'text-blue-600' },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-sm font-bold ${s.color} truncate`}>{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Rechercher un collaborateur..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {filteredAgents.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><Users className="w-14 h-14 mx-auto mb-3 opacity-40" /><p>Aucun collaborateur</p></div>
      ) : (
        <Tabs defaultValue="bulletins" className="space-y-4">
          <TabsList>
            <TabsTrigger value="bulletins" data-testid="tab-bulletins">Bulletins du mois</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves">Soldes de conges</TabsTrigger>
          </TabsList>
          <TabsContent value="bulletins" className="space-y-2">
            {filteredAgents.map(agent => {
            const p = payslipByUserId[agent.id];
            return (
              <Card key={agent.id} className="hover:shadow-md transition-shadow" data-testid={`payslip-row-${agent.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B3A4B] to-[#142D3A] text-[#D4A745] flex items-center justify-center flex-shrink-0 font-bold">
                        {agent.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 truncate">{agent.name}</span>
                          <Badge variant="outline" className="text-xs">{agent.role}</Badge>
                          {agent.position && <span className="text-xs text-gray-500">{agent.position}</span>}
                        </div>
                        <span className="text-xs text-gray-500 font-mono">{agent.employee_id}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {p ? (
                        <>
                          <div className="text-right min-w-[90px]">
                            <p className="text-sm font-bold text-blue-600">{p.paid_leave_days ? `${p.paid_leave_days}j` : '-'}</p>
                            <p className="text-xs text-gray-500">Conges</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-[#1B3A4B]">{formatFCFA(p.net_salary)}</p>
                            <p className="text-xs text-gray-500">Brut : {formatFCFA(p.gross_salary)}</p>
                          </div>
                          <Badge className={p.paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {p.paid ? 'Paye' : 'Non paye'}
                          </Badge>
                          <div className="flex gap-1">
                            {isSuperAdmin && (
                              <Button
                                variant="outline" size="sm"
                                onClick={() => openAttestationDialog(agent)}
                                className="border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38]/10"
                                data-testid={`attestation-${agent.id}`}
                                title="Attestation d'employeur (PDF officiel)"
                              >
                                <FileText className="w-4 h-4 mr-1" /> Attestation
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleOpenEdit(agent, p)} data-testid={`edit-payslip-${agent.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(p.id, agent.name)} data-testid={`download-payslip-${agent.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDelete(p.id, agent.name)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          {isSuperAdmin && (
                            <Button
                              variant="outline" size="sm"
                              onClick={() => openAttestationDialog(agent)}
                              className="border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38]/10"
                              data-testid={`attestation-${agent.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" /> Attestation
                            </Button>
                          )}
                          <Button
                            onClick={() => handleOpenEdit(agent, null)}
                            className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                            data-testid={`create-payslip-${agent.id}`}
                          >
                            <Plus className="w-4 h-4 mr-2" /> Creer bulletin
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </TabsContent>

          <TabsContent value="leaves">
            <LeaveBalancesPanel selectedMonth={selectedMonth} />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={attestOpen} onOpenChange={(o) => !o && setAttestOpen(false)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2B3A2C]">
              <FileText className="w-5 h-5 text-[#B85C38]" /> Attestation Employeur
            </DialogTitle>
            <DialogDescription>
              Personnalisez le signataire et le matricule avant de générer le PDF officiel.
              {attestAgent && <> Salarié : <b>{attestAgent.name}</b> · <span className="capitalize">{attestAgent.role}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Nom du signataire *</Label>
              <Input
                value={attestForm.signatory_name}
                onChange={(e) => setAttestForm({ ...attestForm, signatory_name: e.target.value })}
                placeholder="Ex: M. Amadou DIALLO"
                data-testid="attest-signatory-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Fonction du signataire</Label>
              <Input
                value={attestForm.signatory_title}
                onChange={(e) => setAttestForm({ ...attestForm, signatory_title: e.target.value })}
                placeholder="Ex: Directeur Général"
                data-testid="attest-signatory-title"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Matricule du salarié</Label>
              <Input
                value={attestForm.employee_id}
                onChange={(e) => setAttestForm({ ...attestForm, employee_id: e.target.value })}
                placeholder="Ex: IMMO-89846E"
                data-testid="attest-employee-id"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Motif (optionnel)</Label>
              <Input
                value={attestForm.reason}
                onChange={(e) => setAttestForm({ ...attestForm, reason: e.target.value })}
                placeholder="Ex: démarches administratives, demande de visa..."
                data-testid="attest-reason"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Apparaîtra dans la formule finale « ... est délivrée pour <i>[motif]</i> et servir et valoir ce que de droit. »
              </p>
            </div>

            {/* Signature manuscrite */}
            <div className="border-t pt-3">
              <Label className="text-xs font-semibold">
                Signature manuscrite (optionnel)
              </Label>
              <div className="mt-2">
                <SignatureField
                  value={attestForm.signature_base64}
                  onChange={(b64) => {
                    setAttestForm((f) => ({ ...f, signature_base64: b64 }));
                    if (b64) localStorage.setItem('gab_last_signature_image', b64);
                    else localStorage.removeItem('gab_last_signature_image');
                  }}
                  testIdPrefix="attest-signature"
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                La dernière signature est mémorisée dans ce navigateur pour être ré-utilisée. Le cachet officiel de l&apos;agence (configurable dans les Paramètres) sera automatiquement apposé à gauche de la signature.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setAttestOpen(false)} disabled={attestLoading}>Annuler</Button>
            <Button
              onClick={submitAttestation}
              disabled={attestLoading || !attestForm.signatory_name.trim()}
              className="bg-[#B85C38] hover:bg-[#8F4426] text-white"
              data-testid="attest-generate-btn"
            >
              {attestLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Générer le PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PayslipEditor
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingPayslip(null); setEditingAgent(null); }}
        payslip={editingPayslip}
        agent={editingAgent}
        period={selectedMonth}
        onSaved={() => { setEditOpen(false); fetchAll(); }}
      />
    </div>
  );
};


// =============== PAYSLIP EDITOR MODAL ===============
const PayslipEditor = ({ open, onClose, payslip, agent, period, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = !!payslip?.id;

  useEffect(() => {
    if (payslip) {
      setForm({ ...payslip });
      // If new payslip (no id yet) and no balance set, fetch the user's current balance
      if (!payslip.id && (payslip.paid_leave_balance === null || payslip.paid_leave_balance === undefined)) {
        api.get(`/users/${payslip.user_id}/profile`).then(res => {
          if (res.data?.leave_balance !== undefined) {
            setForm(f => ({ ...f, paid_leave_balance: res.data.leave_balance || 0 }));
          }
        }).catch(() => {});
      }
    }
  }, [payslip]);

  if (!open || !form) return null;

  const updateLine = (field, idx, key, value) => {
    const arr = [...(form[field] || [])];
    arr[idx] = { ...arr[idx], [key]: key === 'amount' || key === 'rate' || key === 'base' ? (value === '' ? null : parseFloat(value)) : value };
    setForm({ ...form, [field]: arr });
  };
  const addLine = (field, defaults = {}) => {
    setForm({ ...form, [field]: [...(form[field] || []), { label: '', amount: 0, ...defaults }] });
  };
  const removeLine = (field, idx) => {
    const arr = [...(form[field] || [])];
    arr.splice(idx, 1);
    setForm({ ...form, [field]: arr });
  };

  const totalAllowances = (form.allowances || []).reduce((s, a) => s + Number(a.amount || 0), 0);
  const totalSocial = (form.social_contributions || []).reduce((s, a) => s + Number(a.amount || 0), 0);
  const totalDeductions = (form.deductions || []).reduce((s, a) => s + Number(a.amount || 0), 0);
  const paidLeaveAmount = Number(form.paid_leave_amount || 0);
  const gross = Number(form.base_salary || 0) + totalAllowances + paidLeaveAmount;
  const net = gross - totalSocial - totalDeductions;

  // Auto-compute paid leave amount from base salary (1/30 of base per day)
  const computeLeaveAmount = () => {
    const days = Number(form.paid_leave_days || 0);
    const base = Number(form.base_salary || 0);
    if (!days || !base) return;
    const suggested = Math.round((base / 30) * days);
    setForm({ ...form, paid_leave_amount: suggested });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id,
        period: form.period || period,
        base_salary: Number(form.base_salary || 0),
        allowances: form.allowances,
        deductions: form.deductions,
        social_contributions: form.social_contributions,
        days_worked: form.days_worked,
        hours_worked: form.hours_worked,
        paid_leave_days: Number(form.paid_leave_days || 0),
        paid_leave_balance: form.paid_leave_balance !== '' && form.paid_leave_balance !== null ? Number(form.paid_leave_balance) : null,
        paid_leave_amount: Number(form.paid_leave_amount || 0),
        notes: form.notes,
        paid: !!form.paid,
        payment_method: form.payment_method,
      };
      if (isEdit) {
        await api.put(`/payslips/${payslip.id}`, payload);
      } else {
        await api.post('/payslips', payload);
      }
      toast.success('Bulletin enregistre');
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[800px] max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B3A4B]">
            <Wallet className="w-5 h-5" /> Bulletin de paie - {agent?.name}
          </DialogTitle>
          <DialogDescription>Periode : {period}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 px-1 py-2">
          {/* Base */}
          <Card className="border-l-4 border-l-[#1B3A4B]">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Salaire de base (FCFA)</Label>
                  <Input
                    type="number"
                    value={form.base_salary || ''}
                    onChange={e => setForm({ ...form, base_salary: parseFloat(e.target.value) || 0 })}
                    data-testid="base-salary-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Jours travailles</Label>
                  <Input type="number" value={form.days_worked || ''} onChange={e => setForm({ ...form, days_worked: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs">Heures travaillees</Label>
                  <Input type="number" value={form.hours_worked || ''} onChange={e => setForm({ ...form, hours_worked: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allowances */}
          <LineSection
            title="Primes et indemnites"
            color="text-green-600"
            items={form.allowances}
            onUpdate={(idx, k, v) => updateLine('allowances', idx, k, v)}
            onRemove={(idx) => removeLine('allowances', idx)}
            onAdd={() => addLine('allowances', { label: '', amount: 0 })}
            sign="+"
          />

          {/* Paid leave section (NEW) */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-blue-600 flex items-center gap-2">
                  Conges payes
                </h4>
                <Button type="button" variant="outline" size="sm" onClick={computeLeaveAmount} data-testid="compute-leave-amount">
                  Calculer auto (base/30)
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Jours pris ce mois</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={form.paid_leave_days ?? ''}
                    onChange={e => setForm({ ...form, paid_leave_days: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    placeholder="Ex: 2.5"
                    data-testid="paid-leave-days"
                  />
                </div>
                <div>
                  <Label className="text-xs">Solde restant (jours)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={form.paid_leave_balance ?? ''}
                    onChange={e => setForm({ ...form, paid_leave_balance: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="Ex: 22"
                  />
                </div>
                <div>
                  <Label className="text-xs">Indemnite FCFA</Label>
                  <Input
                    type="number"
                    value={form.paid_leave_amount ?? ''}
                    onChange={e => setForm({ ...form, paid_leave_amount: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    data-testid="paid-leave-amount"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                L'indemnite de conges payes est ajoutee au salaire brut. Cliquez "Calculer auto" pour un calcul standard (1/30e de la base par jour).
              </p>
            </CardContent>
          </Card>

          {/* Social */}
          <LineSection
            title="Cotisations sociales (INPS, AMO, ITS...)"
            color="text-orange-600"
            items={form.social_contributions}
            onUpdate={(idx, k, v) => updateLine('social_contributions', idx, k, v)}
            onRemove={(idx) => removeLine('social_contributions', idx)}
            onAdd={() => addLine('social_contributions', { label: '', rate: 0, amount: 0 })}
            showRate
            sign="-"
          />

          {/* Deductions */}
          <LineSection
            title="Autres retenues (avance, absences...)"
            color="text-red-600"
            items={form.deductions}
            onUpdate={(idx, k, v) => updateLine('deductions', idx, k, v)}
            onRemove={(idx) => removeLine('deductions', idx)}
            onAdd={() => addLine('deductions', { label: '', amount: 0 })}
            sign="-"
          />

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          <Card className="bg-gradient-to-r from-[#1B3A4B] to-[#2D5469] text-white">
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between text-sm"><span>Salaire de base</span><b>{formatFCFA(form.base_salary)}</b></div>
              {totalAllowances > 0 && <div className="flex justify-between text-sm text-white/80"><span>+ Primes et indemnites</span><span>{formatFCFA(totalAllowances)}</span></div>}
              {paidLeaveAmount > 0 && <div className="flex justify-between text-sm text-white/80"><span>+ Conges payes ({form.paid_leave_days || 0}j)</span><span>{formatFCFA(paidLeaveAmount)}</span></div>}
              <div className="flex justify-between text-sm pt-2 border-t border-white/20"><span className="font-semibold">Salaire brut</span><b>{formatFCFA(gross)}</b></div>
              <div className="flex justify-between text-sm text-white/80"><span>- Cotisations sociales</span><span>{formatFCFA(totalSocial)}</span></div>
              <div className="flex justify-between text-sm text-white/80"><span>- Retenues</span><span>{formatFCFA(totalDeductions)}</span></div>
              <div className="flex justify-between text-lg pt-2 border-t border-white/20">
                <span className="font-semibold">NET A PAYER</span>
                <span className="font-bold text-[#D4A745]">{formatFCFA(net)}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs">Mode de paiement</Label>
              <Select value={form.payment_method || 'virement'} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="especes">Especes</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={!!form.paid}
                onChange={e => setForm({ ...form, paid: e.target.checked })}
                className="w-4 h-4"
                data-testid="paid-checkbox"
              />
              <span className="text-sm font-medium">Bulletin deja paye</span>
            </label>
          </div>
        </div>

        <div className="pt-3 border-t flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-payslip-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isEdit ? 'Mettre a jour' : 'Creer le bulletin'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LineSection = ({ title, color, items, onUpdate, onRemove, onAdd, showRate, sign }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className={`text-sm font-semibold ${color}`}>{title}</h4>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
        </Button>
      </div>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-gray-400 italic">Aucune ligne</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2">
              <div className={showRate ? 'col-span-5' : 'col-span-8'}>
                <Input placeholder="Libelle" value={it.label || ''} onChange={e => onUpdate(idx, 'label', e.target.value)} />
              </div>
              {showRate && (
                <div className="col-span-3">
                  <Input type="number" placeholder="Taux %" value={it.rate ?? ''} onChange={e => onUpdate(idx, 'rate', e.target.value)} />
                </div>
              )}
              <div className="col-span-3">
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">{sign}</span>
                  <Input
                    type="number"
                    placeholder="Montant"
                    className="pl-6"
                    value={it.amount || ''}
                    onChange={e => onUpdate(idx, 'amount', e.target.value)}
                  />
                </div>
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-red-600">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default Payslips;


// =============== LEAVE BALANCES PANEL ===============
const LeaveBalancesPanel = ({ selectedMonth }) => {
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState([]);
  const [accruing, setAccruing] = useState(false);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const res = await api.get('/leave-balances');
      setBalances(res.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalances(); }, []);

  const handleAccrue = async () => {
    if (!window.confirm(`Accumuler les conges du mois ${selectedMonth} pour tous les collaborateurs ? (idempotent, ne double pas)`)) return;
    setAccruing(true);
    try {
      const res = await api.post('/leave-balances/accrue', { period_label: selectedMonth });
      const ok = res.data.results.filter(r => r.status === 'ok').length;
      const skipped = res.data.results.filter(r => r.status === 'deja_accumule').length;
      toast.success(`${ok} collaborateur(s) credite(s), ${skipped} deja accumule(s) ce mois`);
      fetchBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setAccruing(false);
    }
  };

  const handleAdjust = async (userId, name) => {
    const value = window.prompt(`Ajuster le solde de conges de ${name} (+ credit / - debit, en jours)`, '0');
    if (value === null || value === '') return;
    const delta = parseFloat(value);
    if (isNaN(delta) || delta === 0) return;
    const reason = window.prompt('Raison (optionnel)', 'Ajustement manuel') || 'Ajustement manuel';
    try {
      await api.post(`/leave-balances/${userId}/adjust`, { delta, reason });
      toast.success('Solde ajuste');
      fetchBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#1B3A4B]" /></div>;

  return (
    <div className="space-y-4">
      <Card className="bg-gradient-to-r from-blue-50 to-white border-blue-200">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-[#1B3A4B] flex items-center gap-2">
              <CalendarClock className="w-5 h-5" /> Accumulation mensuelle
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Credite chaque collaborateur de son taux d'accumulation (par defaut 2.5 j/mois selon le Code du travail malien).
            </p>
          </div>
          <Button onClick={handleAccrue} disabled={accruing} className="bg-blue-600 hover:bg-blue-700" data-testid="accrue-btn">
            {accruing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
            Accumuler pour {selectedMonth}
          </Button>
        </CardContent>
      </Card>

      {balances.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Aucun collaborateur</div>
      ) : (
        <div className="space-y-2">
          {balances.map(b => (
            <Card key={b.id} data-testid={`balance-row-${b.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B3A4B] to-[#142D3A] text-[#D4A745] flex items-center justify-center flex-shrink-0 font-bold">
                      {b.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 truncate">{b.name}</span>
                        {b.position && <span className="text-xs text-gray-500">{b.position}</span>}
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{b.employee_id}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${b.leave_balance < 0 ? 'text-red-600' : b.leave_balance > 15 ? 'text-green-600' : 'text-[#1B3A4B]'}`}>
                        {b.leave_balance.toFixed(1)} j
                      </p>
                      <p className="text-xs text-gray-500">Solde • +{b.leave_accrual_rate}j/mois</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleAdjust(b.id, b.name)} data-testid={`adjust-balance-${b.id}`}>
                      Ajuster
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
